/**
 * ОСНОВА.Помощник — общий чат-помощник для любого сотрудника.
 * Зеркало DTO `/api/v1/assistant/*` (snake_case 1:1 с backend
 * `osnova_storage::assistant`).
 */

/** Чат сотрудника с Помощником. Чужие чаты не видны никому. */
export interface AssistantSession {
  id: string;
  user_id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

/**
 * На что опирался ответ: фрагмент нормативной базы, ЛНА или файл из «Общих»
 * на ОСНОВА.Диске (`type: 'disk'`, `url` — адрес файла в окне Диска).
 */
export interface AssistantSource {
  title: string;
  type?: string;
  url?: string;
}

export interface AssistantMessage {
  id: string;
  session_id: string;
  role: 'user' | 'assistant';
  content: string;
  /** Раздел интерфейса, откуда задан вопрос (только у `user`). */
  page: string | null;
  /** Только у `assistant`; пустой массив — нормы не понадобились. */
  sources: AssistantSource[];
  created_at: string;
  /** Оценка ответа владельцем чата: 1 — полезно, -1 — не полезно, null — нет оценки. */
  feedback: AssistantFeedbackValue | null;
  /** «Что не так» — необязательный комментарий к оценке. */
  feedback_comment: string | null;
  feedback_at: string | null;
}

export type AssistantFeedbackValue = 1 | -1;

/** Наибольшая длина комментария к оценке — как `MAX_FEEDBACK_COMMENT_CHARS` на backend. */
export const ASSISTANT_MAX_FEEDBACK_COMMENT = 1000;

/** Ответ «не полезно» с вопросом — для администратора. Кто спрашивал — только ролями. */
export interface AssistantDislikedItem {
  answer_id: string;
  answered_at: string;
  question: string | null;
  /** Раздел, откуда спросили, — названием. */
  section: string | null;
  answer: string;
  comment: string | null;
  roles: string[];
}

/** Оценки ответов Помощника за период (`GET /admin/assistant/feedback`). */
export interface AssistantFeedbackReport {
  days: number;
  answers: number;
  helpful: number;
  not_helpful: number;
  disliked: AssistantDislikedItem[];
}

/** Ответ `POST /assistant/ask`: чат (новый, если вопрос его создал) и оба сообщения. */
export interface AssistantAskResponse {
  session: AssistantSession;
  question: AssistantMessage;
  answer: AssistantMessage;
}

/** Максимальная длина вопроса — как `MAX_QUESTION_CHARS` на backend. */
export const ASSISTANT_MAX_QUESTION = 4000;

/** HTTP-статус ошибки RTK Query (`null` — сеть или не HTTP). */
export function assistantErrorStatus(err: unknown): number | null {
  if (typeof err !== 'object' || err === null || !('status' in err)) return null;
  const s = (err as { status?: unknown }).status;
  return typeof s === 'number' ? s : null;
}

/** Понятный текст ошибки ответа Помощника. */
export function assistantErrorText(err: unknown): string {
  const status = assistantErrorStatus(err);
  if (status === 429) return 'Лимит вопросов на сегодня исчерпан — попробуйте завтра.';
  if (status === 400 || status === 422) {
    const data = (err as { data?: { error?: { message?: unknown } } }).data;
    const m = data?.error?.message;
    return typeof m === 'string' && m.trim() ? m : 'Вопрос не принят — проверьте текст.';
  }
  if (status === 404) return 'Этот чат не найден — возможно, он удалён.';
  if (status !== null && status >= 500) {
    return 'Помощник не смог ответить. Вопрос сохранён в чате — попробуйте переспросить чуть позже.';
  }
  return 'Нет связи с сервером. Проверьте подключение и попробуйте ещё раз.';
}

/** Вопрос не дошёл до сохранения на сервере — текст стоит вернуть в поле ввода. */
export function assistantQuestionLost(err: unknown): boolean {
  const status = assistantErrorStatus(err);
  return status === null || status === 400 || status === 422 || status === 429;
}
